import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import { Shield } from "lucide-react";
import { AdminCatches } from "@/components/admin/AdminCatches";
import { AdminUsers } from "@/components/admin/AdminUsers";
import { AdminWeighStations } from "@/components/admin/AdminWeighStations";
import { AdminNavigation } from "@/components/admin/AdminNavigation";
import { AdminFishSpecies } from "@/components/admin/AdminFishSpecies";
import { AdminSystemHealth } from "@/components/admin/AdminSystemHealth";
import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate("/auth");
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('has_role', { _user_id: user.id, _role: 'admin' });

        if (error) throw error;

        if (!data) {
          navigate("/dashboard");
          return;
        }

        setIsAdmin(true);
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">Admin Dashboard</h1>
          </div>
          <p className="text-muted-foreground">
            Manage catches, users, and weigh stations
          </p>
        </div>

        <Tabs defaultValue="catches" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="catches">Catches</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="weigh-stations">Weigh Stations</TabsTrigger>
            <TabsTrigger value="fish-species">Fish Species</TabsTrigger>
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
            <TabsTrigger value="system">System Health</TabsTrigger>
          </TabsList>

          <TabsContent value="catches">
            <AdminCatches />
          </TabsContent>

          <TabsContent value="users">
            <AdminUsers />
          </TabsContent>

          <TabsContent value="weigh-stations">
            <AdminWeighStations />
          </TabsContent>

          <TabsContent value="fish-species">
            <AdminFishSpecies />
          </TabsContent>

          <TabsContent value="navigation">
            <AdminNavigation />
          </TabsContent>

          <TabsContent value="system">
            <AdminSystemHealth />
          </TabsContent>
        </Tabs>
      </main>

      {/* Sentry Test Button - Only visible in development */}
      {!import.meta.env.PROD && (
        <Button
          onClick={() => {
            throw new Error('This is your first error!');
          }}
          variant="destructive"
          size="sm"
          className="fixed bottom-4 right-4 shadow-lg z-50"
        >
          Break the world
        </Button>
      )}
    </div>
  );
};

export default Admin;
